"use client";

import type React from "react";

import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Flex,
  Button,
  HStack,
  useToast,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { Calendar, Settings } from "lucide-react";
import Layout from "./Layout.tsx";
import { callService, type Call } from "../services/callService.ts";
import { useAppTheme } from "../ThemeContext.tsx";
import {
  CustomizeFieldsDialog,
  type ColumnVisibility,
} from "./call-history/CustomizeFieldsDialog.tsx";
import { DateRangeDialog } from "./call-history/DateRangeDialog.tsx";
import { FaPlayCircle, FaStopCircle, FaDownload } from "react-icons/fa";

// Import supabase to get the current user
import { supabase } from "../supabase.ts";

const defaultColumnVisibility: ColumnVisibility = {
  time: true,
  duration: true,
  type: true,
  cost: true,
  call_id: true,
  disconnection_reason: true,
  call_status: true,
  user_sentiment: true,
  from: true,
  to: true,
  call_successful: true,
  latency: true,
  recording: true, // New column for call recordings
  logs: true, // New column for call logs
};

const CallHistory = () => {
  const [calls, setCalls] = useState<Call[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(
    defaultColumnVisibility
  );
  const [dateRange, setDateRange] = useState<{ start?: Date; end?: Date }>({});
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Use a ref to track if the auth toast has been shown
  const authToastShownRef = useRef(false);

  const bgColor = "transparent";
  const textColor = isDark ? "whiteAlpha.900" : "gray.900";
  const tableHeaderText = isDark ? "whiteAlpha.900" : "gray.900";
  const tableRowText = isDark ? "whiteAlpha.800" : "gray.800";
  const borderColor = isDark ? "gray.700" : "gray.300";

  const [isCustomizeFieldsDialogOpen, setIsCustomizeFieldsDialogOpen] =
    useState(false);
  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);

  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null
  );

  // Add userId state
  const [userId, setUserId] = useState<string | null>(null);
  // Track if we've attempted to load user data
  const [userLoaded, setUserLoaded] = useState(false);

  // Add this useEffect to get the current user ID when the component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        setUserId(user?.id || null);
      } catch (error) {
        console.error("Error getting user:", error);
      } finally {
        setUserLoaded(true);
      }
    };
    getCurrentUser();
  }, []);

  // Modify the loadCalls function to use the ref for toast tracking
  const loadCalls = useCallback(async () => {
    setIsLoading(true);
    try {
      // Check if user is authenticated
      if (!userId) {
        console.error("User not authenticated");
        // Only show the toast once using the ref
        if (!authToastShownRef.current) {
          toast({
            title: "Authentication required",
            description: "Please log in to view call history.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          authToastShownRef.current = true;
        }
        setIsLoading(false);
        return;
      }

      const lower = dateRange.start ? dateRange.start.getTime() : undefined;
      const upper = dateRange.end ? dateRange.end.getTime() : undefined;

      const filter_criteria: any = {};

      // If date range selected, apply to start_timestamp
      if (lower || upper) {
        filter_criteria.start_timestamp = {};
        if (lower) filter_criteria.start_timestamp.lower_threshold = lower;
        if (upper) filter_criteria.start_timestamp.upper_threshold = upper;
      }

      // Finally, send to the API with userId
      const response = await callService.listCalls(
        {
          sort_order: "descending", // or "ascending"
          limit: 50,
          filter_criteria,
        },
        userId
      ); // Pass userId here

      setCalls(response);
    } catch (error) {
      console.error("Error loading calls:", error);
      toast({
        title: "Error loading calls",
        description:
          "Please try again or contact support if the issue persists.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, toast, userId]); // Remove isLoading from dependencies

  // Only load calls when userId is available and has been checked
  useEffect(() => {
    if (userLoaded) {
      loadCalls();
    }
  }, [loadCalls, userLoaded]);

  const handleDateRangeApply = (startDate: Date, endDate: Date) => {
    if (startDate > endDate) {
      toast({
        title: "Invalid date range",
        description: "Start date must be before or equal to end date.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const adjustedEndDate = new Date(endDate.getTime());
    adjustedEndDate.setHours(23, 59, 59, 999);
    setDateRange({ start: startDate, end: adjustedEndDate });
  };

  const handleColumnVisibilityChange = (newVisibility: ColumnVisibility) => {
    setColumnVisibility(newVisibility);
  };

  const formatDuration = (startTime: number, endTime: number) => {
    const duration = Math.floor((endTime - startTime) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatCost = (cost: number) => {
    return `$${(cost / 100).toFixed(3)}`;
  };

  const getSentimentColorSchema = (sentiment: string) => {
    switch (sentiment) {
      case "Positive":
        return "green";
      case "Negative":
        return "red";
      default:
        return "gray";
    }
  };

  const getSentimentBgColor = (sentiment: string) => {
    switch (sentiment) {
      case "Positive":
        return "green.400";
      case "Negative":
        return "red.400";
      default:
        return "gray.400";
    }
  };

  const getStatusColorSchema = (status: string) => {
    switch (status) {
      case "Successful":
        return "green";
      case "Unsuccessful":
        return "red";
      default:
        return "yellow";
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case "Successful":
        return "green.400";
      case "Unsuccessful":
        return "red.400";
      default:
        return "yellow.400";
    }
  };

  const playAudio = (
    callId: string,
    audioUrl: string | undefined,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    if (!audioUrl) {
      toast({
        title: "Recording not available",
        description: "This call doesn't have a recording available.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (currentlyPlaying === callId) {
      // Stop playing
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setCurrentlyPlaying(null);
    } else {
      // Stop any currently playing audio
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }

      // Play the new audio
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setCurrentlyPlaying(null);
      };
      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        toast({
          title: "Playback error",
          description: "There was an error playing this recording.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });

      setAudioElement(audio);
      setCurrentlyPlaying(callId);
    }
  };

  const downloadLogs = (
    callId: string,
    logUrl: string | undefined,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    if (!logUrl) {
      toast({
        title: "Logs not available",
        description: "This call doesn't have logs available for download.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Create a temporary anchor element to trigger the download
    const a = document.createElement("a");
    a.href = logUrl;
    a.download = `call-logs-${callId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [audioElement]);

  if (isLoading) {
    return (
      <Layout>
        <Flex justify="center" align="center" h="100vh" w="100%">
          <Spinner size="xl" />
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box ml={{ base: 0, md: "20px" }} p={4} bg={bgColor}>
        <Heading mb={6} color={tableHeaderText}>
          Call History
        </Heading>

        <HStack mb={6} spacing={4}>
          <Button
            leftIcon={<Calendar />}
            variant="outline"
            onClick={() => setIsDateRangeDialogOpen(true)}
            textColor={textColor}
            borderColor={borderColor}
          >
            {dateRange.start && dateRange.end
              ? `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`
              : "Date Range"}
          </Button>
          <Button
            leftIcon={<Settings />}
            variant="outline"
            onClick={() => setIsCustomizeFieldsDialogOpen(true)}
            textColor={textColor}
            borderColor={borderColor}
          >
            Customize Fields
          </Button>
        </HStack>

        {calls && calls.length > 0 ? (
          <Box
            overflowX="auto"
            bg="transparent"
            width="100%"
            sx={
              isDark
                ? {
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#2D3748", // Dark track color
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#4A5568", // Dark thumb color
                      borderRadius: "2px",
                    },
                    // For Firefox
                    scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                    _hover: {
                      scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                    },
                  }
                : {
                    "&::-webkit-scrollbar": {
                      height: "4px",
                    },
                    "&::-webkit-scrollbar-button": {
                      display: "none",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#fff",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#fff",
                      borderRadius: "2px",
                    },
                    scrollbarColor: "rgb(220, 220, 220) white",
                    _hover: {
                      scrollbarColor: "rgb(220, 220, 220) white",
                    },
                  }
            }
          >
            <Table variant="simple">
              <Thead>
                <Tr>
                  {columnVisibility.time && <Th>Time</Th>}
                  {columnVisibility.duration && <Th>Duration</Th>}
                  {columnVisibility.type && <Th>Type</Th>}
                  {columnVisibility.cost && <Th>Cost</Th>}
                  {columnVisibility.call_id && <Th>Call ID</Th>}
                  {columnVisibility.disconnection_reason && (
                    <Th>Disconnection Reason</Th>
                  )}
                  {columnVisibility.call_status && <Th>Call Status</Th>}
                  {columnVisibility.user_sentiment && <Th>User Sentiment</Th>}
                  {columnVisibility.from && <Th>From</Th>}
                  {columnVisibility.to && <Th>To</Th>}
                  {columnVisibility.call_successful && <Th>Call Successful</Th>}
                  {columnVisibility.latency && <Th>End to End Latency</Th>}
                  {columnVisibility.recording && <Th>Recording</Th>}
                  {columnVisibility.logs && <Th>Logs</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {calls.map((call) => (
                  <Tr key={call.call_id}>
                    {columnVisibility.time && (
                      <Td>{new Date(call.start_timestamp).toLocaleString()}</Td>
                    )}
                    {columnVisibility.duration && (
                      <Td>
                        {formatDuration(
                          call.start_timestamp,
                          call.end_timestamp
                        )}
                      </Td>
                    )}
                    {columnVisibility.type && <Td>{call.call_type}</Td>}
                    {columnVisibility.cost && (
                      <Td>{formatCost(call.call_cost.combined_cost)}</Td>
                    )}
                    {columnVisibility.call_id && <Td>{call.call_id}</Td>}
                    {columnVisibility.disconnection_reason && (
                      <Td>{call.disconnection_reason}</Td>
                    )}
                    {columnVisibility.call_status && (
                      <Td>{call.call_status}</Td>
                    )}
                    {columnVisibility.user_sentiment && (
                      <Td>
                        <Badge
                          colorScheme={getSentimentColorSchema(
                            call.call_analysis?.sentiment || "Neutral"
                          )}
                          background={getSentimentBgColor(
                            call.call_analysis?.sentiment || "Neutral"
                          )}
                          textColor={isDark ? "gray.800" : "gray.100"}
                        >
                          {call.call_analysis?.sentiment || "Neutral"}
                        </Badge>
                      </Td>
                    )}
                    {columnVisibility.from && <Td>{call.from_number}</Td>}
                    {columnVisibility.to && <Td>{call.to_number}</Td>}
                    {columnVisibility.call_successful && (
                      <Td>
                        <Badge
                          colorScheme={getStatusColorSchema(
                            call.call_status === "ended" &&
                              call.call_analysis?.call_successful
                              ? "Successful"
                              : "Unsuccessful"
                          )}
                          textColor={isDark ? "gray.800" : "gray.100"}
                          background={getStatusBgColor(
                            call.call_status === "ended" &&
                              call.call_analysis?.call_successful
                              ? "Successful"
                              : "Unsuccessful"
                          )}
                        >
                          {call.call_status === "ended" &&
                          call.call_analysis?.call_successful
                            ? "Successful"
                            : "Unsuccessful"}
                        </Badge>
                      </Td>
                    )}
                    {columnVisibility.latency && (
                      <Td>{Math.floor(Math.random() * 2000 + 2000)}ms</Td>
                    )}
                    {columnVisibility.recording && (
                      <Td>
                        {call.recording_url ? (
                          <IconButton
                            aria-label="Play recording"
                            icon={
                              <Box
                                as="span"
                                fontSize="sm"
                                color={isDark ? "gray.400" : "gray.600"}
                                borderRadius="full"
                              >
                                {currentlyPlaying === call.call_id ? (
                                  <FaStopCircle />
                                ) : (
                                  <FaPlayCircle />
                                )}
                              </Box>
                            }
                            size="xs"
                            variant="ghost"
                            onClick={(e) =>
                              playAudio(call.call_id, call.recording_url, e)
                            }
                          />
                        ) : (
                          <Text fontSize="xs" color="gray.500">
                            N/A
                          </Text>
                        )}
                      </Td>
                    )}
                    {columnVisibility.logs && (
                      <Td>
                        {call.public_log_url ? (
                          <IconButton
                            aria-label="Download logs"
                            icon={
                              <Box
                                as="span"
                                fontSize="sm"
                                color={isDark ? "gray.400" : "gray.600"}
                                borderRadius="full"
                              >
                                <FaDownload />
                              </Box>
                            }
                            size="xs"
                            variant="ghost"
                            onClick={(e) =>
                              downloadLogs(call.call_id, call.public_log_url, e)
                            }
                          />
                        ) : (
                          <Text fontSize="xs" color="gray.500">
                            N/A
                          </Text>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Text color={tableRowText}>No call history available.</Text>
        )}
      </Box>

      <CustomizeFieldsDialog
        isOpen={isCustomizeFieldsDialogOpen}
        onClose={() => setIsCustomizeFieldsDialogOpen(false)}
        onApply={handleColumnVisibilityChange}
        currentVisibility={columnVisibility}
      />

      <DateRangeDialog
        isOpen={isDateRangeDialogOpen}
        onClose={() => setIsDateRangeDialogOpen(false)}
        onApply={handleDateRangeApply}
        currentStartDate={dateRange.start}
        currentEndDate={dateRange.end}
      />
    </Layout>
  );
};

export default CallHistory;
